import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchGroupByIdAsync, updateGroupAsync, clearError, clearCurrentGroup, fetchGroupsAsync } from '../store/slices/groupsSlice';
import { useLevels } from '../hooks/useLevels';
import { fetchStudentsAsync, createStudentAsync, updateStudentAsync, clearError as clearStudentError } from '../store/slices/studentsSlice';
import type { Student } from '../store/slices/studentsSlice';
import LoadingSpinner from './LoadingSpinner';
import { formatDateEuropean } from '../utils/dateUtils';
import './GroupForm.css';
import axios from 'axios';

interface Schedule {
  dayOfWeek: string;
  time: string;
}

const EditGroup: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { currentGroup, loading, error, groups: allGroups } = useAppSelector((state) => state.groups);
  const { students, loading: studentsLoading, error: studentsError } = useAppSelector((state) => state.students);

  const [formData, setFormData] = useState({
    name: '',
    level: '',
    startDate: '',
    endDate: '',
    offerNumber: '',
    parentGroupId: '',
    hiddenFromLanding: false,
  });

  const [schedules, setSchedules] = useState<Schedule[]>([
    { dayOfWeek: '', time: '' }
  ]);
  const [originalScheduleCount, setOriginalScheduleCount] = useState(0);

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [successMessage, setSuccessMessage] = useState<string | undefined>(undefined);
  
  // Student management states
  const [showAddStudent, setShowAddStudent] = useState(false);
  const [addMode, setAddMode] = useState<'new' | 'existing'>('existing'); // Default to existing
  const [newStudent, setNewStudent] = useState({
    firstName: '',
    lastName: '',
    surname: '',
    contacts: '',
    startDate: '',
  });
  const [studentFormErrors, setStudentFormErrors] = useState<Record<string, string>>({});
  
  // Existing student search
  const [searchQuery, setSearchQuery] = useState('');
  const [availableStudents, setAvailableStudents] = useState<Array<{id: number, fullName: string, isQuitted: boolean}>>([]);
  const [selectedStudentId, setSelectedStudentId] = useState<number | null>(null);
  const [loadingStudents, setLoadingStudents] = useState(false);


  const daysOfWeek = [
    { value: 'monday', label: 'Monday' },
    { value: 'tuesday', label: 'Tuesday' },
    { value: 'wednesday', label: 'Wednesday' },
    { value: 'thursday', label: 'Thursday' },
    { value: 'friday', label: 'Friday' },
    { value: 'saturday', label: 'Saturday' },
    { value: 'sunday', label: 'Sunday' },
  ];

  const levels = useLevels();

  const formatTimeFromAPI = (timeString: string) => {
    // If time includes seconds (HH:MM:SS), strip them to HH:MM
    if (timeString && timeString.includes(':')) {
      const parts = timeString.split(':');
      if (parts.length >= 2) {
        return `${parts[0]}:${parts[1]}`;
      }
    }
    return timeString;
  };

  // Load group data on component mount
  useEffect(() => {
    if (id) {
      dispatch(fetchGroupByIdAsync(parseInt(id)));
    }
    
    return () => {
      dispatch(clearCurrentGroup());
    };
  }, [dispatch, id]);

  // Load all groups for Parent Group dropdown
  useEffect(() => {
    dispatch(fetchGroupsAsync({ page: 1, limit: 1000 }));
  }, [dispatch]);

  // Populate form when group data is loaded. Done during render (guarded by
  // isInitialLoad so it runs once) rather than in an effect, so it stays in
  // sync without cascading renders.
  if (currentGroup && isInitialLoad) {
    setFormData({
      name: currentGroup.name || '',
      level: currentGroup.level,
      startDate: currentGroup.startDate ? (currentGroup.startDate.includes('T') ? currentGroup.startDate.split('T')[0] : currentGroup.startDate) : '',
      endDate: currentGroup.endDate ? (currentGroup.endDate.includes('T') ? currentGroup.endDate.split('T')[0] : currentGroup.endDate) : '',
      offerNumber: currentGroup.offerNumber != null ? String(currentGroup.offerNumber) : '',
      parentGroupId: currentGroup.parentGroupId != null ? String(currentGroup.parentGroupId) : '',
      hiddenFromLanding: !!currentGroup.hiddenFromLanding,
    });

    if (currentGroup.schedules && currentGroup.schedules.length > 0) {
      const loadedSchedules = currentGroup.schedules.map(schedule => ({
        dayOfWeek: schedule.dayOfWeek,
        time: formatTimeFromAPI(schedule.time)
      }));
      setSchedules(loadedSchedules);
      setOriginalScheduleCount(loadedSchedules.length);
    } else {
      setOriginalScheduleCount(0);
    }

    setIsInitialLoad(false);
  }

  // Load students for this group
  useEffect(() => {
    if (currentGroup?.id) {
      dispatch(fetchStudentsAsync({ groupId: currentGroup.id, limit: 100 }));
    }
  }, [dispatch, currentGroup?.id]);

  // Fetch available students when search query changes
  useEffect(() => {
    const fetchAvailableStudents = async () => {
      if (!currentGroup?.id || addMode !== 'existing') return;
      
      setLoadingStudents(true);
      try {
        const { data } = await axios.post(
          `${import.meta.env.VITE_API_URL}/groups/available-students`,
          {
            groupId: currentGroup.id,
            search: searchQuery
          }
        );
        setAvailableStudents(data.students);
      } catch (error) {
        console.error('Failed to fetch available students:', error);
      } finally {
        setLoadingStudents(false);
      }
    };

    const debounceTimer = setTimeout(fetchAvailableStudents, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchQuery, currentGroup?.id, addMode]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear form error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Format date as yyyy-mm-dd (no brackets), handling ISO strings with/without time
  const toYMD = (dateInput?: string | Date | null) => {
    if (!dateInput) return '';
    if (typeof dateInput === 'string') {
      if (dateInput.includes('T')) return dateInput.split('T')[0];
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateInput)) return dateInput;
    }
    const d = new Date(dateInput as Date);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const formatTimeInput = (value: string) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    
    // Format as HH:MM
    if (digits.length >= 3) {
      return `${digits.slice(0, 2)}:${digits.slice(2, 4)}`;
    } else if (digits.length >= 1) {
      return digits;
    }
    return '';
  };



  const handleScheduleChange = (index: number, field: 'dayOfWeek' | 'time', value: string) => {
    const newSchedules = [...schedules];
    
    if (field === 'time') {
      // Format the time input
      const formattedTime = formatTimeInput(value);
      newSchedules[index] = {
        ...newSchedules[index],
        [field]: formattedTime
      };
    } else {
      newSchedules[index] = {
        ...newSchedules[index],
        [field]: value
      };
    }
    
    setSchedules(newSchedules);
  };

  const addSchedule = () => {
    setSchedules([...schedules, { dayOfWeek: '', time: '' }]);
  };

  const removeSchedule = (index: number) => {
    if (schedules.length > 1) {
      setSchedules(schedules.filter((_, i) => i !== index));
    }
  };

  // Student management functions
  const handleStudentInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setNewStudent(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear form error when user starts typing
    if (studentFormErrors[name]) {
      setStudentFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateStudentForm = () => {
    const errors: Record<string, string> = {};

    if (!newStudent.firstName.trim()) {
      errors.firstName = 'First name is required';
    }

    if (!newStudent.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }

    if (!newStudent.startDate) {
      errors.startDate = 'First payment date is required';
    }

    setStudentFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddStudent = async () => {
    if (addMode === 'new') {
      if (!validateStudentForm() || !currentGroup) {
        return;
      }

      try {
        await dispatch(createStudentAsync({
          ...newStudent,
          groupId: currentGroup.id,
          firstName: newStudent.firstName.trim(),
          lastName: newStudent.lastName.trim(),
          surname: newStudent.surname.trim() || undefined,
          contacts: newStudent.contacts.trim() || undefined,
        })).unwrap();

        // Reset form and close
        setNewStudent({
          firstName: '',
          lastName: '',
          surname: '',
          contacts: '',
          startDate: '',
        });
        setShowAddStudent(false);
        setStudentFormErrors({});

        // Refresh students list
        dispatch(fetchStudentsAsync({ groupId: currentGroup.id, limit: 100 }));
      } catch (error) {
        console.error('Failed to add student:', error);
      }
    } else {
      // Add existing student
      if (!selectedStudentId || !currentGroup) {
        return;
      }

      try {
        await axios.post(
          `${import.meta.env.VITE_API_URL}/groups/${currentGroup.id}/students`,
          { studentId: selectedStudentId }
        );

        // Reset and close
        setSearchQuery('');
        setSelectedStudentId(null);
        setShowAddStudent(false);
        
        // Refresh students list
        dispatch(fetchStudentsAsync({ groupId: currentGroup.id, limit: 100 }));
      } catch (error) {
        console.error('Failed to add student:', error);
      }
    }
  };

  const handleQuitStudent = async (studentId: number, quitDate: string | null) => {
    try {
      await dispatch(updateStudentAsync({
        id: studentId,
        studentData: { quitDate: quitDate ?? null }
      })).unwrap();
      if (currentGroup) {
        dispatch(fetchStudentsAsync({ groupId: currentGroup.id, limit: 100 }));
      }
      return true;
    } catch {
      return false;
    }
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!formData.level) {
      errors.level = 'Level is required';
    }

    if (!formData.startDate) {
      errors.startDate = 'Start date is required';
    }

    // End date is optional, but if provided must be after start date
    if (formData.startDate && formData.endDate) {
      if (new Date(formData.endDate) <= new Date(formData.startDate)) {
        errors.endDate = 'End date must be after start date';
      }
    }

    // Validate schedules
    const validSchedules = schedules.filter(schedule => 
      schedule.dayOfWeek && schedule.time
    );

    if (validSchedules.length === 0) {
      errors.schedules = 'At least one complete schedule is required';
    }

    // Check for duplicate day/time combinations
    const scheduleKeys = validSchedules.map(s => `${s.dayOfWeek}-${s.time}`);
    const uniqueKeys = new Set(scheduleKeys);
    if (scheduleKeys.length !== uniqueKeys.size) {
      errors.schedules = 'Duplicate schedule entries are not allowed';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm() || !currentGroup) {
      return;
    }

    const validSchedules = schedules.filter(schedule => 
      schedule.dayOfWeek && schedule.time
    );

    const groupData = {
      name: formData.name.trim() || undefined,
      level: formData.level,
      startDate: formData.startDate,
      endDate: formData.endDate || null,
      schedules: validSchedules,
      offerNumber: formData.offerNumber === '' ? null : parseInt(formData.offerNumber, 10),
      parentGroupId: formData.parentGroupId === '' ? null : parseInt(formData.parentGroupId, 10),
      hiddenFromLanding: formData.hiddenFromLanding,
    };

    try {
      // Update the group first
      const response = await dispatch(updateGroupAsync({
        id: currentGroup.id,
        groupData
      })).unwrap() as { attachedStudentsCount?: number; message?: string };

      // Show success message if students were auto-attached from parent group
      const attachedCount = response.attachedStudentsCount || 0;
      if (attachedCount > 0) {
        setSuccessMessage(response.message || `${attachedCount} students attached from parent group`);
        // Auto-hide success message after 5 seconds
        setTimeout(() => {
          setSuccessMessage(undefined);
        }, 5000);
      }
      
      // Refresh the group data to show updated changes
      await dispatch(fetchGroupByIdAsync(currentGroup.id));
      await dispatch(fetchStudentsAsync({ groupId: currentGroup.id, limit: 100 }));
    } catch (error) {
      // Error is handled by the slice
    }
  };

  const handleCancel = () => {
    navigate('/groups');
  };

  if (loading && isInitialLoad) {
    return <LoadingSpinner />;
  }

  if (!currentGroup && !loading) {
    return (
      <div className="group-form-container">
        <div className="error-message">
          Group not found.
          <button onClick={() => navigate('/groups')}>Go back to groups</button>
        </div>
      </div>
    );
  }

  return (
    <div className="group-form-container">
      <div className="page-header">
        <div className="page-header-content">
          <h1>Edit Group</h1>
          <p>Update group information and schedule</p>
        </div>
        <button 
          type="button" 
          className="btn-secondary"
          onClick={handleCancel}
        >
          Cancel
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => dispatch(clearError())} className="close-error">×</button>
        </div>
      )}

      {successMessage && (
        <div className="success-message" style={{ 
          backgroundColor: '#d4edda', 
          color: '#155724', 
          padding: '1rem', 
          borderRadius: 'var(--radius)', 
          marginBottom: '1rem',
          border: '1px solid #c3e6cb',
          position: 'relative'
        }}>
          {successMessage}
          <button 
            onClick={() => setSuccessMessage(undefined)} 
            className="close-error"
            style={{ color: '#155724' }}
          >×</button>
        </div>
      )}

      <form onSubmit={handleSubmit} className="group-form">
        <div className="form-section">
          <h2>Basic Information</h2>
          
          <div className="form-group">
            <label htmlFor="name">Group Name (Optional)</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Enter group name (optional)"
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="offerNumber">Offer Number (Optional)</label>
            <input
              type="number"
              id="offerNumber"
              name="offerNumber"
              value={formData.offerNumber}
              onChange={handleInputChange}
              placeholder="Enter offer number"
              className="form-input"
              min={1}
            />
          </div>

          <div className="form-group">
            <label htmlFor="parentGroupId">Parent Group (Optional)</label>
            <select
              id="parentGroupId"
              name="parentGroupId"
              value={formData.parentGroupId}
              onChange={handleInputChange}
              className="form-select"
            >
              <option value="">None</option>
              {allGroups
                .filter(g => !currentGroup || g.id !== currentGroup.id)
                .map(g => (
                  <option key={g.id} value={g.id}>
                    {g.name ? `${g.name} (${g.id})` : `Group #${g.id}`} — {g.level.toUpperCase()} — {toYMD(g.startDate)} - {g.endDate ? toYMD(g.endDate) : '-'}
                  </option>
                ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="level">Level *</label>
            <select
              id="level"
              name="level"
              value={formData.level}
              onChange={handleInputChange}
              className={`form-select ${formErrors.level ? 'error' : ''}`}
              required
            >
              <option value="">Select level</option>
              {levels.map(level => (
                <option key={level.value} value={level.value}>
                  {level.label}
                </option>
              ))}
            </select>
            {formErrors.level && <span className="error-text">{formErrors.level}</span>}
          </div>

          <div className="form-row-compact">
            <div className="form-group">
              <label htmlFor="startDate">Start Date *</label>
              <input
                type="date"
                id="startDate"
                name="startDate"
                value={formData.startDate}
                onChange={handleInputChange}
                className={`form-input ${formErrors.startDate ? 'error' : ''}`}
                required
              />
              {formErrors.startDate && <span className="error-text">{formErrors.startDate}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="endDate">End Date (Optional)</label>
              <input
                type="date"
                id="endDate"
                name="endDate"
                value={formData.endDate}
                onChange={handleInputChange}
                className={`form-input ${formErrors.endDate ? 'error' : ''}`}
              />
              {formErrors.endDate && <span className="error-text">{formErrors.endDate}</span>}
            </div>
          </div>

          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={formData.hiddenFromLanding}
                onChange={(e) => setFormData(prev => ({ ...prev, hiddenFromLanding: e.target.checked }))}
                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
              />
              Hide from Landing
            </label>
          </div>
        </div>

        <div className="form-section">
          <div className="section-header">
            <h2>Schedule</h2>
            <button
              type="button"
              onClick={addSchedule}
              className="btn-add-schedule"
            >
              + Add Schedule
            </button>
          </div>

          {formErrors.schedules && (
            <div className="error-text schedule-error">{formErrors.schedules}</div>
          )}

          <table className="schedule-table">
            <thead>
              <tr>
                <th>Day of Week</th>
                <th>Time</th>
                <th style={{ width: '50px' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {schedules.map((schedule, index) => (
                <tr key={index} className={index >= originalScheduleCount ? 'schedule-row-new' : ''}>
                  <td>
                    <select
                      value={schedule.dayOfWeek}
                      onChange={(e) => handleScheduleChange(index, 'dayOfWeek', e.target.value)}
                      className="form-select"
                    >
                      <option value="">Select day</option>
                      {daysOfWeek.map(day => (
                        <option key={day.value} value={day.value}>
                          {day.label}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <input
                      type="text"
                      value={schedule.time}
                      onChange={(e) => handleScheduleChange(index, 'time', e.target.value)}
                      className="form-input"
                      pattern="^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$"
                      placeholder="HH:MM (24-hour format)"
                      title="Please enter time in 24-hour format (HH:MM)"
                      maxLength={5}
                    />
                  </td>
                  <td>
                    {schedules.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeSchedule(index)}
                        className="btn-remove-schedule"
                        title="Remove this schedule"
                      >
                        ×
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="form-section">
          <div className="section-header">
            <h2>Students</h2>
            <button
              type="button"
              onClick={() => setShowAddStudent(true)}
              className="btn-add-schedule"
            >
              + Add Student
            </button>
          </div>

          {studentsError && (
            <div className="error-message">
              {studentsError}
              <button onClick={() => dispatch(clearStudentError())} className="close-error">×</button>
            </div>
          )}

          {showAddStudent && (
            <div className="student-item">
              <div className="student-form">
                {/* Mode Toggle */}
                <div className="form-group" style={{ marginBottom: '1rem' }}>
                  <div style={{ display: 'flex', gap: '1rem', borderBottom: '2px solid #e2e8f0', paddingBottom: '0.5rem' }}>
                    <button
                      type="button"
                      onClick={() => setAddMode('existing')}
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: '0.5rem 1rem',
                        cursor: 'pointer',
                        fontWeight: addMode === 'existing' ? '600' : '400',
                        color: addMode === 'existing' ? '#667eea' : '#6b7280',
                        borderBottom: addMode === 'existing' ? '2px solid #667eea' : 'none',
                        marginBottom: '-2px'
                      }}
                    >
                      Add Existing Student
                    </button>
                    <button
                      type="button"
                      onClick={() => setAddMode('new')}
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: '0.5rem 1rem',
                        cursor: 'pointer',
                        fontWeight: addMode === 'new' ? '600' : '400',
                        color: addMode === 'new' ? '#667eea' : '#6b7280',
                        borderBottom: addMode === 'new' ? '2px solid #667eea' : 'none',
                        marginBottom: '-2px'
                      }}
                    >
                      Create New Student
                    </button>
                  </div>
                </div>

                {addMode === 'existing' ? (
                  <>
                    {/* Search and Select Existing Student */}
                    <div className="form-group">
                      <label>Search Student</label>
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="form-input"
                        placeholder="Type to search by name..."
                      />
                    </div>

                    {loadingStudents && (
                      <p style={{ color: '#6b7280', fontStyle: 'italic' }}>Loading students...</p>
                    )}

                    {!loadingStudents && availableStudents.length > 0 && (
                      <div className="form-group">
                        <label>Select Student</label>
                        <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 'var(--radius)' }}>
                          {availableStudents.map(student => (
                            <div
                              key={student.id}
                              onClick={() => setSelectedStudentId(student.id)}
                              style={{
                                padding: '0.75rem',
                                cursor: 'pointer',
                                background: selectedStudentId === student.id ? '#f0f4ff' : 'white',
                                borderBottom: '1px solid #e2e8f0',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                              }}
                            >
                              <span>{student.fullName}</span>
                              {student.isQuitted && (
                                <span style={{ fontSize: '0.75rem', color: '#e53e3e', fontWeight: '500' }}>
                                  (Quitted)
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {!loadingStudents && searchQuery && availableStudents.length === 0 && (
                      <p style={{ color: '#6b7280', fontStyle: 'italic' }}>No students found matching "{searchQuery}"</p>
                    )}
                  </>
                ) : (
                  <>
                    {/* Create New Student Form */}
                    <div className="form-row-compact three-columns">
                  <div className="form-group">
                    <label>First Name *</label>
                    <input
                      type="text"
                      name="firstName"
                      value={newStudent.firstName}
                      onChange={handleStudentInputChange}
                      className={`form-input ${studentFormErrors.firstName ? 'error' : ''}`}
                      required
                    />
                    {studentFormErrors.firstName && <span className="error-text">{studentFormErrors.firstName}</span>}
                  </div>

                  <div className="form-group">
                    <label>Last Name *</label>
                    <input
                      type="text"
                      name="lastName"
                      value={newStudent.lastName}
                      onChange={handleStudentInputChange}
                      className={`form-input ${studentFormErrors.lastName ? 'error' : ''}`}
                      required
                    />
                    {studentFormErrors.lastName && <span className="error-text">{studentFormErrors.lastName}</span>}
                  </div>

                  <div className="form-group">
                    <label>Surname</label>
                    <input
                      type="text"
                      name="surname"
                      value={newStudent.surname}
                      onChange={handleStudentInputChange}
                      className="form-input"
                      placeholder="Enter surname (optional)"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Contacts</label>
                  <textarea
                    name="contacts"
                    value={newStudent.contacts}
                    onChange={handleStudentInputChange}
                    className="form-input"
                    placeholder="Enter contact information (phone, email, etc.)"
                    rows={2}
                  />
                </div>

                <div className="form-group">
                  <label>First Payment Date *</label>
                  <input
                    type="date"
                    name="startDate"
                    value={newStudent.startDate}
                    onChange={handleStudentInputChange}
                    className={`form-input ${studentFormErrors.startDate ? 'error' : ''}`}
                    required
                  />
                  {studentFormErrors.startDate && <span className="error-text">{studentFormErrors.startDate}</span>}
                </div>
                  </>
                )}

                <div className="student-actions">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddStudent(false);
                      setNewStudent({ firstName: '', lastName: '', surname: '', contacts: '', startDate: '' });
                      setStudentFormErrors({});
                      setSearchQuery('');
                      setSelectedStudentId(null);
                    }}
                    className="btn-secondary btn-small"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleAddStudent}
                    className="btn-success btn-small"
                    disabled={studentsLoading || (addMode === 'existing' && !selectedStudentId)}
                  >
                    {studentsLoading ? 'Adding...' : addMode === 'existing' ? 'Add Student' : 'Create & Add Student'}
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="students-list">
            {students.length === 0 ? (
              <p style={{ color: '#6b7280', fontStyle: 'italic' }}>No students in this group yet.</p>
            ) : (
              students
                .slice()
                .sort((a: Student, b: Student) => {
                  const aIsQuitted = !!a.quitDate;
                  const bIsQuitted = !!b.quitDate;

                  if (aIsQuitted === bIsQuitted) return 0;
                  return aIsQuitted ? 1 : -1;
                })
                .map((student: Student) => (
                  <StudentItem
                    key={student.id}
                    student={student}
                    onQuitChange={handleQuitStudent}
                  />
                ))
            )}
          </div>
        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={handleCancel}
            className="btn-secondary"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-success"
            disabled={loading}
          >
            {loading ? 'Updating...' : 'Update Group'}
          </button>
        </div>
      </form>
    </div>
  );
};

// Student Item Component for EditGroup
interface StudentItemProps {
  student: Student;
  onQuitChange: (studentId: number, quitDate: string | null) => Promise<boolean>;
}

const StudentItem: React.FC<StudentItemProps> = ({ student, onQuitChange }) => {
  const [quitDate, setQuitDate] = useState(student.quitDate ? student.quitDate.split('T')[0] : '');
  const [isQuittedChecked, setIsQuittedChecked] = useState(!!student.quitDate);
  const [saving, setSaving] = useState(false);
  const [statusMessage, setStatusMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  // Re-sync the quit fields whenever the incoming student's quitDate changes.
  // Done during render (guarded by a tracked previous value) rather than in an
  // effect, to keep state in sync without cascading renders.
  const [prevQuitDate, setPrevQuitDate] = useState(student.quitDate);
  if (prevQuitDate !== student.quitDate) {
    setPrevQuitDate(student.quitDate);
    setIsQuittedChecked(!!student.quitDate);
    setQuitDate(student.quitDate ? student.quitDate.split('T')[0] : '');
  }

  const showStatus = (text: string, type: 'success' | 'error') => {
    setStatusMessage({ text, type });
    setTimeout(() => setStatusMessage(null), 3000);
  };

  const handleQuitToggle = async () => {
    const newCheckedState = !isQuittedChecked;
    setIsQuittedChecked(newCheckedState);
    setSaving(true);

    let dateToSend: string | null;
    if (!newCheckedState) {
      dateToSend = null;
    } else {
      dateToSend = quitDate || new Date().toISOString().split('T')[0];
      if (!quitDate) setQuitDate(dateToSend);
    }

    const success = await onQuitChange(student.id, dateToSend);
    setSaving(false);
    showStatus(
      success ? 'Student status updated' : 'Failed to update status',
      success ? 'success' : 'error'
    );
    if (!success) {
      setIsQuittedChecked(!newCheckedState);
    }
  };

  const handleQuitDateChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = e.target.value;
    setQuitDate(newDate);

    if (isQuittedChecked && newDate) {
      setSaving(true);
      const success = await onQuitChange(student.id, newDate);
      setSaving(false);
      showStatus(
        success ? 'Quit date updated' : 'Failed to update quit date',
        success ? 'success' : 'error'
      );
    }
  };

  const formatDate = (dateString: string) => {
    return formatDateEuropean(dateString);
  };

  const isQuitted = isQuittedChecked && (student.quitDate || quitDate);

  return (
    <div className={`student-item ${isQuitted ? 'student-item-quitted' : ''}`}>
      <div className="student-info">
        <div className="student-header">
          <h4>
            <span>{student.firstName} {student.lastName}{student.surname ? ` ${student.surname}` : ''}</span>
            {isQuitted && (
              <span className="quit-date-inline">
                ({student.quitDate ? formatDate(student.quitDate) : 'Pending'})
              </span>
            )}
          </h4>
          <div className="student-status">
            {saving && <span style={{ color: '#666', fontSize: '0.8rem', marginRight: '0.5rem' }}>Saving...</span>}
            {statusMessage && (
              <span style={{
                fontSize: '0.8rem',
                marginRight: '0.5rem',
                color: statusMessage.type === 'success' ? '#155724' : '#721c24',
                backgroundColor: statusMessage.type === 'success' ? '#d4edda' : '#f8d7da',
                padding: '2px 8px',
                borderRadius: 'var(--radius)',
              }}>
                {statusMessage.text}
              </span>
            )}
            {isQuitted ? (
              <span className="status-quit">
                Quit
              </span>
            ) : (
              <span className="status-active">Active</span>
            )}
          </div>
        </div>
        
        <div className="student-details">
          <p><strong>First Payment Date:</strong> {formatDate(student.startDate)}</p>
          {student.contacts && (
            <p><strong>Contacts:</strong> {student.contacts}</p>
          )}
        </div>

        <div className="student-quit-section">
          <label className="checkbox-control">
            <input
              type="checkbox"
              checked={isQuittedChecked}
              onChange={handleQuitToggle}
            />
            <span className="checkbox-control__label">Student has quitted</span>
          </label>

          {isQuittedChecked && (
            <div className="form-group quit-date-input">
              <label htmlFor={`quit-date-${student.id}`}>Quit date</label>
              <input
                type="date"
                id={`quit-date-${student.id}`}
                value={quitDate}
                onChange={handleQuitDateChange}
                className="form-input"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EditGroup; 